import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Divider,
  Paper,
  Grid,
  Chip,
  Autocomplete,
  TextField
} from '@mui/material';
import { 
  Close, 
  Check, 
  Person, 
  Email, 
  Phone, 
  DirectionsBus, 
  ConfirmationNumber,
  Business,
  Info
} from '@mui/icons-material';
import { SignUpData, Trip, Apartment } from '@/types';
import MemberStatusChip from '@/components/ui/MemberStatusChip';
import { useI18n } from '@/hooks/useI18n';

interface ConvertSignUpDialogProps {
  open: boolean;
  signUp: SignUpData | null;
  trip: Trip;
  apartments: Apartment[];
  onClose: () => void;
  onConfirm: (apartmentId: string, apartmentTypeId: string | null, skiPassType: string | undefined, transportationType: string, isMember: boolean) => void;
}

const ConvertSignUpDialog: React.FC<ConvertSignUpDialogProps> = ({
  open,
  signUp,
  trip,
  apartments,
  onClose,
  onConfirm
}) => {
  const [selectedApartmentId, setSelectedApartmentId] = useState<string>('');
  const [selectedApartmentTypeId, setSelectedApartmentTypeId] = useState<string>('');
  const [selectedSkiPassType, setSelectedSkiPassType] = useState<string>('');
  const [selectedTransportationType, setSelectedTransportationType] = useState<string>('');
  const [isMember, setIsMember] = useState(false);
  const { t } = useI18n();

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open && signUp) {
      setSelectedApartmentId('');
      setSelectedApartmentTypeId(signUp.preferredApartmentTypeId || '');
      setSelectedSkiPassType(signUp.primaryParticipant.preferredSkiPass || '');
      setSelectedTransportationType(signUp.preferredTransportationId || '');
      setIsMember(signUp.primaryParticipant.isSkiClubMember);
    }
  }, [open, signUp]);

  if (!signUp) return null;

  const preferredApartmentType = trip.apartmentTypes.find(at => at.id === signUp.preferredApartmentTypeId);
  const preferredSkiPass = trip.skiPasses.find(sp => sp.id === signUp.primaryParticipant.preferredSkiPass);
  const preferredTransportation = trip.transportationOptions.find(to => to.id === signUp.preferredTransportationId);

  const handleConfirm = () => {
    if (!selectedApartmentId) return;
    onConfirm(selectedApartmentId, selectedApartmentTypeId, selectedSkiPassType, selectedTransportationType, isMember);
  };

  const getApartmentTypeForApartment = (apartmentId: string) => {
    const apartment = apartments.find(a => a.id === apartmentId);
    return apartment ? trip.apartmentTypes.find(at => at.id === apartment.apartmentTypeId) : null;
  };

  // Prepare apartment options for Autocomplete
  const apartmentOptions = apartments.map(apartment => {
    const apartmentType = getApartmentTypeForApartment(apartment.id);
    const isPreferred = apartmentType?.id === signUp.preferredApartmentTypeId;
    return {
      id: apartment.id,
      label: `${apartment.name} (${apartment.currentOccupancy}/${apartment.maxOccupancy})`,
      name: apartment.name,
      occupancy: `${apartment.currentOccupancy}/${apartment.maxOccupancy}`,
      typeName: apartmentType?.name || 'Unknown',
      isPreferred
    };
  });

  const selectedApartment = apartmentOptions.find(opt => opt.id === selectedApartmentId) || null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography variant="h6" component="div">
          {t('signup.convertDialog.title')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 2 }}>
        {/* Static Information Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info color="primary" />
            {t('signup.convertDialog.signUpInformation')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person fontSize="small" />
                  {t('common.name')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {signUp.primaryParticipant.name}
                  </Typography>
                  <MemberStatusChip isMember={signUp.primaryParticipant.isSkiClubMember} />
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Email fontSize="small" />
                  {t('common.email')}
                </Typography>
                <Typography variant="body1">{signUp.email}</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone fontSize="small" />
                  {t('common.phone')}
                </Typography>
                <Typography variant="body1">{signUp.phone}</Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Business fontSize="small" />
                  {t('signup.convertDialog.preferredApartmentType')}
                </Typography>
                <Typography variant="body1">{preferredApartmentType?.name || t('common.na')}</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ConfirmationNumber fontSize="small" />
                  {t('signup.convertDialog.preferredSkiPass')}
                </Typography>
                <Typography variant="body1">{preferredSkiPass?.name || t('signup.convertDialog.noneSelected')}</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DirectionsBus fontSize="small" />
                  {t('signup.convertDialog.preferredTransportation')}
                </Typography>
                <Typography variant="body1">{preferredTransportation?.name || t('common.na')}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('signup.convertDialog.assignmentAdjustments')}
          </Typography>
        </Divider>

        {/* Assignment Section */}
        <Box sx={{ space: 2 }}>
          <Autocomplete
            fullWidth
            sx={{ mb: 2 }}
            options={apartmentOptions}
            value={selectedApartment}
            onChange={(_, newValue) => setSelectedApartmentId(newValue?.id || '')}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('signup.convertDialog.assignToApartment')}
                required
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name} ({option.occupancy})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('signup.convertDialog.type')}: {option.typeName}
                    </Typography>
                  </Box>
                  {option.isPreferred && (
                    <Chip 
                      label={t('signup.convertDialog.preferred')} 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={t('signup.convertDialog.noApartmentsAvailable')}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="skipass-select-label">{t('skipass.skiPass')}</InputLabel>
            <Select
              labelId="skipass-select-label"
              value={selectedSkiPassType}
              onChange={(e) => setSelectedSkiPassType(e.target.value)}
              label={t('skipass.skiPass')}
            >
              <MenuItem value="">{t('skipass.noSkiPass')}</MenuItem>
              {trip.skiPasses.map(skiPass => (
                <MenuItem key={skiPass.id} value={skiPass.id}>
                  {skiPass.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="transportation-select-label">{t('transportation.transportation')}</InputLabel>
            <Select
              labelId="transportation-select-label"
              value={selectedTransportationType}
              onChange={(e) => setSelectedTransportationType(e.target.value)}
              label={t('transportation.transportation')}
            >
              {trip.transportationOptions.map(transport => (
                <MenuItem key={transport.id} value={transport.id}>
                  {transport.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={isMember}
                onChange={(e) => setIsMember(e.target.checked)}
              />
            }
            label={t('participant.skiClubMember')}
            sx={{ mt: 1 }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          startIcon={<Check />}
          disabled={!selectedApartmentId}
        >
          {t('signup.convertDialog.convertToTraveller')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertSignUpDialog;
